import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { PaymentDeviceRejectionReason } from '@/models/common/PaymentDeviceEnums';
import { PaymentDeviceRentalRequestDtoIn } from '@/models/in/PaymentDeviceDtoIn';
import { RejectPaymentDeviceRequestDtoOut } from '@/models/out/PaymentDeviceDtoOut';
import { MANAGEMENT_DEVICE_REQUESTS_URL, paymentDeviceErrorMessage } from '@/helpers/paymentDevices';
import usePaymentDeviceApi from '../usePaymentDeviceApi';
import { DialogActions, ErrorLine, NoteField, RadioGroup } from './dialogParts';

interface RejectRequestDialogProps {
  request: PaymentDeviceRentalRequestDtoIn | null;
  onClose: () => void;
}

/**
 * Declines a request. The usual reason - no free terminal - is the default and carries its fixed
 * sentence to the coordinator; any other reason has to be written out, since it is all the
 * coordinator will be told.
 */
const RejectRequestDialog = ({ request, onClose }: RejectRequestDialogProps) => {
  const { t } = useTranslation();
  const api = usePaymentDeviceApi();

  const [reason, setReason] = useState<PaymentDeviceRejectionReason>(PaymentDeviceRejectionReason.NoDevices);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setReason(PaymentDeviceRejectionReason.NoDevices);
    setText('');
    setError(null);
  }, [request]);

  if (!request) return null;

  const handleSubmit = async () => {
    if (reason === PaymentDeviceRejectionReason.Other && !text.trim()) {
      setError(t('paymentDevices.dialogs.rejectTextRequired'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await api.post<PaymentDeviceRentalRequestDtoIn, RejectPaymentDeviceRequestDtoOut>(
        `${MANAGEMENT_DEVICE_REQUESTS_URL}/${request.id}/reject`,
        { reason, text: text.trim() || null },
      );
      onClose();
    } catch (err) {
      setError(paymentDeviceErrorMessage(err, t, 'paymentDevices.errors.save'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MainDialogLayout
      open
      onOpenChange={(isOpen: boolean) => { if (!isOpen) onClose(); }}
      title={t('paymentDevices.dialogs.rejectTitle', { id: request.id })}
      maxWidth="520px"
      fitContent
    >
      <div className="flex flex-col gap-4 p-4">
        <RadioGroup
          name="pd-reject-reason"
          label={t('paymentDevices.dialogs.rejectReason')}
          value={reason}
          onChange={setReason}
          options={[
            { value: PaymentDeviceRejectionReason.NoDevices, label: t('paymentDevices.rejectionReason.NoDevices') },
            { value: PaymentDeviceRejectionReason.Other, label: t('paymentDevices.rejectionReason.Other') },
          ]}
        />
        <NoteField
          id="pd-reject-text"
          label={reason === PaymentDeviceRejectionReason.Other ? t('paymentDevices.dialogs.rejectText') : t('paymentDevices.dialogs.notesOptional')}
          value={text}
          onChange={setText}
        />
        <ErrorLine error={error} />
        <DialogActions onCancel={onClose} onConfirm={handleSubmit} isSubmitting={isSubmitting} confirmText={t('paymentDevices.actions.reject')} />
      </div>
    </MainDialogLayout>
  );
};

export default RejectRequestDialog;
